import { MenuItem } from "primereact/menuitem";
import { LDFlagSet } from "launchdarkly-js-client-sdk";
import { findIndex } from "lodash";
import MenuSeparator from "./MenuSeparator";

export const SOURCE = "accelerate";

const AUTHCHECK_PATH = "/authcheck";

export interface INavItem {
  label: string;
  path: string;
  permission: string[];
  selected?: boolean;
  flag?: string;
  invertFeatureFlag?: boolean;
  icon?: string;
  showIfAnyPermission?: boolean;
}

/**
 * Add new app codes here
 */
export enum ApplicationCode {
  REPORTS = "rep",
  DASHBOARD = "dash",
  CHECKLIST = "cklst",
  CALENDAR = "caldr",
  ACTION_ITEMS = "acitm",
  COMPLIANCE = "compl",
  PERFORMANCE_METRICS = "pmet",
  ASSETS = "asset",
  INCIDENTS = "saf",
  GHG = "ghg",
  CHEM = "chem",
  STRATRATEGY_MANAGEMENT = "Strat",
  EHS = "v5",
  TAL = "tal_embed",
  OFFICE_ERGO = "oergo_embed",
  INDUSTRIAL_ERGO = "ergo_embed",
  COW = "cow_embed",
}

//IAM-4132: Do not show checklist in unpurchased capabilities when unassigned
//IAM-5012: Do not show reports in unpurchased capabilities when unassigned
//IAM-5034: Do not show dashboard in unpurchased capabilities when unassigned
//IAM-5037: DO not show Calendar in unpurchased capabilities when unassigned
//IAM-5036: Do not show assets in unpurchased capabilities when unassigned
//IAM-5235: Do not show Action Items in unpurchased capabilities when unassigned
export const OVERRIDE_UNPURCHASED = [
  ApplicationCode.CHECKLIST,
  ApplicationCode.REPORTS,
  ApplicationCode.DASHBOARD,
  ApplicationCode.CALENDAR,
  ApplicationCode.ACTION_ITEMS,
  ApplicationCode.TAL,
  ApplicationCode.OFFICE_ERGO,
  ApplicationCode.INDUSTRIAL_ERGO,
  ApplicationCode.COW,
  ApplicationCode.ASSETS,
] as string[];

export interface Application {
  id: string;
  redirectUrl: string;
  embedUrl: string;
  capabilityPath: string;
  relativeApplicationPath: string;
  applicationCode: string;
  applicationName: string;
  solutionName: string;
  assigned: boolean;
  marketingUrl: string;
  accelerateNative: boolean;
}

export interface ApplicationItem extends MenuItem, Partial<Application> {
  className?: string;
}

export interface ISolutionsMenuItem extends MenuItem {
  id: string;
  name?: string;
  label?: string;
  code?: string;
  icon?: string;
  className?: string;
  items?: ApplicationItem[];
  applications?: Application[];
}

export const isOverrideUnpurchased = (application: Application): boolean => {
  if (application.assigned) return false;
  return OVERRIDE_UNPURCHASED.includes(application.applicationCode);
};

export const addApplicationClassName = (application: Application) => {
  return application.assigned ? "" : "unpurchased";
};

export const isDisabledByFeatureFlag = (
  application: Application,
  featureFlags: LDFlagSet
): boolean => {
  switch (application.applicationCode) {
    case ApplicationCode.REPORTS:
      return !featureFlags["productPlatformMenuReportsDisplay"];
    case ApplicationCode.DASHBOARD:
      return !featureFlags["productPlatformMenuDashboardDisplay"];
    case ApplicationCode.CALENDAR:
      return !featureFlags["productCalendar"];
    case ApplicationCode.ASSETS:
      return !featureFlags["productAssetsLibrary"];
    case ApplicationCode.COMPLIANCE:
      return !featureFlags["productShowCompliance"];
    case ApplicationCode.STRATRATEGY_MANAGEMENT:
      return !featureFlags["productStrategyManagement"];
    case ApplicationCode.TAL:
      return !featureFlags["showTALNav"];
    case ApplicationCode.OFFICE_ERGO:
      return !featureFlags["showOfficeErgoNav"];
    case ApplicationCode.INDUSTRIAL_ERGO:
      return !featureFlags["showIndustrialErgoNav"];
    case ApplicationCode.COW:
      return !featureFlags["showEmbeddedCOWNav"];
    default:
      return;
  }
};

export const isDisabledByPermission = (
  application: Application,
  permissions: any
): boolean => {
  if (application.applicationCode === ApplicationCode.REPORTS) {
    return !permissions.canExploreReportingData;
  }
  return false;
};

export const addSolutionClassNames = (
  solutions: ISolutionsMenuItem[]
): ISolutionsMenuItem[] => {
  return solutions.map((sol) => ({
    ...sol,
    className: "unpurchased",
  }));
};

export const removeEmptySolutions = (
  solutions: ISolutionsMenuItem[]
): ISolutionsMenuItem[] => {
  return solutions.filter((solution) => solution.items.length);
};

export const setCapabilityDestination = (
  application: Application,
  history: any
) => {
  if (application.assigned === false) {
    return () => (window.open().location.href = application.marketingUrl);
  }

  if (application.accelerateNative && application.capabilityPath) {
    return () => {
      history.push(application.capabilityPath);
    };
  }

  if (!!application.embedUrl) {
    return () =>
      history.push(
        `${application.capabilityPath}${application.embedUrl}${application.relativeApplicationPath}`,
        { source: SOURCE }
      );
  }

  if (application.applicationCode === ApplicationCode.CHEM) {
    return () => history.push("/chemmanagement");
  }

  // TEMP: this prevents the user from getting nested navigation menus when a redirect url is
  // constructed with an embedUrl of null.
  // Long term, it would be better to have IAM not construct the redirectUrl in this manner if
  // the embedUrl is null.
  const appToUse = new URL(application.redirectUrl).searchParams.get("app");
  if (appToUse && appToUse.startsWith("null")) {
    return () => history.push("/forbidden");
  }

  const url = new URL(application.redirectUrl);

  if (url.pathname === AUTHCHECK_PATH) {
    return () => history.push(`${url.pathname}${url.search}`);
  }

  return () => (window.location.href = application.redirectUrl);
};

export const addSolutionSeparator = (
  solutions: ISolutionsMenuItem[],
  includeUnpurchased: boolean
) => {
  const index = findIndex(solutions, { className: "unpurchased" });

  if (!includeUnpurchased && index !== -1) {
    return solutions.slice(0, index);
  }

  if (index !== -1) {
    const separator = {
      id: "solution_separator",
      items: [],
      template: <MenuSeparator section="Solution" />,
    };
    solutions.splice(index, 0, separator);
  }
  return solutions;
};

export const sortApplications = (applications: ApplicationItem[]): void => {
  applications.sort((a, b) => {
    const aIsUnpurchased = a.className === "unpurchased" ? 1 : 0;
    const bIsUnpurchased = b.className === "unpurchased" ? 1 : 0;

    return aIsUnpurchased - bIsUnpurchased;
  });
};

export const addApplicationSeparator = (
  solution: ISolutionsMenuItem,
  includeUnpurchased: boolean
): void => {
  const index = findIndex(solution.items, { className: "unpurchased" });

  if (!includeUnpurchased && index !== -1) {
    solution.items = solution.items.slice(0, index);
    return;
  }

  if (index !== -1 && index !== 0) {
    const separator = {
      id: "application_separator",
      items: [],
      template: <MenuSeparator section="Capability" />,
    };
    solution.items.splice(index, 0, separator);
  }
};
